import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback, useRef } from 'react';
import ReactFlow, {
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    MarkerType,
    Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Button, Tooltip, Modal, message, Card, Typography } from 'antd';
import { SettingOutlined, DeleteOutlined } from '@ant-design/icons';

import {
    ClearOutlined,
    MenuOutlined,
    CloseOutlined
} from '@ant-design/icons';

// Import custom nodes
import TriggerNode from './nodes/TriggerNode';
import ConditionalNode from './nodes/ConditionalNode';
import SwitchNode from './nodes/SwitchNode';
import ActionNode from './nodes/ActionNode';
import CompleteNode from './nodes/CompleteNode';
import ErrorNode from './nodes/ErrorNode';
import NodePalette from './WorkflowNodePalette';

const { Text } = Typography;

// Register custom node types
const nodeTypes = {
    triggerNode: TriggerNode,
    conditionalNode: ConditionalNode,
    switchNode: SwitchNode,
    actionNode: ActionNode,
    completeNode: CompleteNode,
    errorNode: ErrorNode
};

// Initial nodes for a blank workflow
const getInitialNodes = (position = { x: 250, y: 50 }) => [
    {
        id: 'trigger-1',
        type: 'triggerNode',
        data: {
            label: 'Trigger',
            triggerType: 'manualTrigger',
            config: { type: 'manualTrigger' }
        },
        position
    }
];

const getInitialPosition = (containerRect, paletteVisible) => {
    const paletteWidth = paletteVisible ? 250 : 0;
    const availableWidth = containerRect.width - paletteWidth;
    // Use the same formula as your initial render:
    const initialX = (availableWidth / 2) + 50;
    const initialY = 50;  // Always at 50 (top of the available area)
    return { x: initialX, y: initialY };
};


// Find a free connection point on a node
const findFreeConnectionPoint = (node, direction = 'output', edges = []) => {
    if (!node) return null;

    // Get connected edges
    const nodeEdges = direction === 'output'
        ? edges.filter(e => e.source === node.id)
        : edges.filter(e => e.target === node.id);

    // Check node type for available connection points
    switch (node.type) {
        case 'triggerNode':
            // Trigger nodes only have one output and no inputs
            return direction === 'output' && !nodeEdges.find(e => e.sourceHandle === 'a')
                ? { id: 'a', handle: 'a' }
                : null;

        case 'conditionalNode':
            if (direction === 'input') {
                // Conditional nodes have one input
                return !nodeEdges.find(e => e.target === node.id) ? { id: 'in', handle: 'in' } : null;
            } else {
                // Conditional nodes have true/false outputs
                if (!nodeEdges.find(e => e.source === node.id && e.sourceHandle === 'true')) {
                    return { id: 'true', handle: 'true' };
                }
                if (!nodeEdges.find(e => e.source === node.id && e.sourceHandle === 'false')) {
                    return { id: 'false', handle: 'false' };
                }
                return null;
            }

        case 'switchNode':
            if (direction === 'input') {
                // Switch nodes have one input
                return !nodeEdges.find(e => e.target === node.id) ? { id: 'in', handle: 'in' } : null;
            } else {
                // Switch nodes have multiple outputs based on configuration
                const outputs = node.data.outputs || node.data.config?.outputs || ['Option 1', 'Option 2', 'Default'];
                for (const output of outputs) {
                    const handleId = `switch-${output}`;
                    if (!nodeEdges.find(e => e.source === node.id && e.sourceHandle === handleId)) {
                        return { id: handleId, handle: handleId };
                    }
                }
                return null;
            }

        case 'actionNode':
            if (direction === 'input') {
                // Action nodes have one input
                return !nodeEdges.find(e => e.target === node.id) ? { id: 'in', handle: 'in' } : null;
            } else {
                // Action nodes have one output
                return !nodeEdges.find(e => e.source === node.id && e.sourceHandle === 'out')
                    ? { id: 'out', handle: 'out' }
                    : null;
            }

        case 'completeNode':
        case 'errorNode':
            // Complete/Error nodes only have inputs, no outputs
            return direction === 'input' && !nodeEdges.find(e => e.target === node.id)
                ? { id: 'in', handle: 'in' }
                : null;

        default:
            return null;
    }
};

// Get the height of a node based on its type and data
const getNodeHeight = (node) => {
    if (!node) return 100;
    // Query the DOM for the node element by its data-id attribute.
    const nodeElement = document.querySelector(`.react-flow__node[data-id="${node.id}"]`);
    if (nodeElement) {
        return nodeElement.getBoundingClientRect().height;
    }
    // Fallback if not rendered yet:
    switch (node.type) {
        case 'switchNode': {
            const outputs = node.data.outputs || node.data.config?.outputs || ['Option 1', 'Option 2', 'Default'];
            return 150 + Math.max(0, outputs.length - 3) * 30;
        }
        case 'triggerNode':
            return 100;
        case 'conditionalNode':
            return 120;
        case 'actionNode':
            return 120;
        case 'completeNode':
        case 'errorNode':
            return 80;
        default:
            return 100;
    }
};


// Try to create a connection between selected node and new node
const tryConnectNodes = (selectedNode, newNode, edges = []) => {
    if (!selectedNode || !newNode) return null;

    // For trigger nodes, always connect from trigger to new node
    if (selectedNode.type === 'triggerNode') {
        const outputHandle = findFreeConnectionPoint(selectedNode, 'output', edges);
        const newNodeInputHandle = findFreeConnectionPoint(newNode, 'input', edges);

        if (outputHandle && newNodeInputHandle) {
            return {
                id: `edge-${selectedNode.id}-${outputHandle.handle}-${newNode.id}`,
                source: selectedNode.id,
                sourceHandle: outputHandle.handle,
                target: newNode.id,
                targetHandle: newNodeInputHandle.handle,
                type: 'default',
                markerEnd: { type: MarkerType.ArrowClosed },
                style: { strokeWidth: 2 }
            };
        }
        return null;
    }

    // For terminal nodes (complete/error), try to connect the new node to them
    if (selectedNode.type === 'completeNode' || selectedNode.type === 'errorNode') {
        const newNodeOutputHandle = findFreeConnectionPoint(newNode, 'output', edges);
        const inputHandle = findFreeConnectionPoint(selectedNode, 'input', edges);

        if (newNodeOutputHandle && inputHandle) {
            return {
                id: `edge-${newNode.id}-${newNodeOutputHandle.handle}-${selectedNode.id}`,
                source: newNode.id,
                sourceHandle: newNodeOutputHandle.handle,
                target: selectedNode.id,
                targetHandle: inputHandle.handle,
                type: 'default',
                markerEnd: { type: MarkerType.ArrowClosed },
                style: { strokeWidth: 2 }
            };
        }
        return null;
    }

    // For all other node types, first try to connect from selected to new
    const outputHandle = findFreeConnectionPoint(selectedNode, 'output', edges);
    const newNodeInputHandle = findFreeConnectionPoint(newNode, 'input', edges);

    if (outputHandle && newNodeInputHandle) {
        return {
            id: `edge-${selectedNode.id}-${outputHandle.handle}-${newNode.id}`,
            source: selectedNode.id,
            sourceHandle: outputHandle.handle,
            target: newNode.id,
            targetHandle: newNodeInputHandle.handle,
            type: 'default',
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { strokeWidth: 2 }
        };
    }

    // If that doesn't work, try the reverse connection
    const newNodeOutputHandle = findFreeConnectionPoint(newNode, 'output', edges);
    const inputHandle = findFreeConnectionPoint(selectedNode, 'input', edges);

    if (newNodeOutputHandle && inputHandle) {
        return {
            id: `edge-${newNode.id}-${newNodeOutputHandle.handle}-${selectedNode.id}`,
            source: newNode.id,
            sourceHandle: newNodeOutputHandle.handle,
            target: selectedNode.id,
            targetHandle: inputHandle.handle,
            type: 'default',
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { strokeWidth: 2 }
        };
    }

    return null;
};

// Get appropriate position for new node relative to selected node
const getPositionForNewNode = (selectedNode, newNodeType, edges = []) => {
    console.log("getPositionForNewNode called", { selectedNode, newNodeType });
    // If no parent is selected, fall back to a default position.
    const defaultPosition = { x: 250, y: 200 };
    if (!selectedNode) return defaultPosition;

    const basePos = selectedNode.position;
    const parentHeight = getNodeHeight(selectedNode);
    console.log("Parent node height : " + parentHeight);
    const parentBottom = basePos.y + parentHeight;
    const verticalOffset = 50; // constant gap from the parent's bottom
    const horizontalSpacing = 280; // default horizontal spacing

    // Special case: if the new node is a trigger, position it above.
    if (newNodeType === 'trigger') {
        return { x: basePos.x, y: basePos.y - verticalOffset };
    }
    // For complete or error nodes, always below.
    if (newNodeType === 'complete' || newNodeType === 'error') {
        return { x: basePos.x, y: parentBottom + verticalOffset };
    }

    // Get the available output handle
    const outputHandle = findFreeConnectionPoint(selectedNode, 'output', edges);
    if (outputHandle) {
        if (outputHandle.id === 'true') {
            // Position to the left
            return { x: basePos.x - horizontalSpacing / 2, y: parentBottom + verticalOffset };
        } else if (outputHandle.id === 'false') {
            // Position to the right
            return { x: basePos.x + horizontalSpacing / 2, y: parentBottom + verticalOffset };
        } else if (outputHandle.id.startsWith('switch-')) {
            // For switch nodes, spread children horizontally evenly
            const outputs = selectedNode.data.outputs || selectedNode.data.config?.outputs || [];
            const currentOutputIndex = outputs.findIndex(o => `switch-${o}` === outputHandle.id);
            if (currentOutputIndex >= 0) {
                const spacingBetweenOutputs = 300; // fixed horizontal gap for switch outputs
                const totalWidth = spacingBetweenOutputs * (outputs.length - 1);
                const offsetX = currentOutputIndex * spacingBetweenOutputs - totalWidth / 2;
                return { x: basePos.x + offsetX, y: parentBottom + verticalOffset };
            }
        }
        // Fallback: position centered below the parent.
        return { x: basePos.x, y: parentBottom + verticalOffset };
    }

    // If the node is to be added as an input (above), use:
    const inputHandle = findFreeConnectionPoint(selectedNode, 'input', edges);
    if (inputHandle && ['trigger', 'conditional', 'action', 'switch'].includes(newNodeType)) {
        return { x: basePos.x, y: basePos.y - verticalOffset };
    }

    // Default to positioning to the right if nothing else matches.
    return { x: basePos.x + horizontalSpacing, y: basePos.y };
};




// Safely pan to show a specific node without changing zoom
const panToNode = (reactFlowInstance, node) => {
    if (!reactFlowInstance || !node) return;

    // Get current viewport
    const { x, y, zoom } = reactFlowInstance.getViewport();

    // Get the DOM element dimensions
    const container = document.querySelector('.react-flow__renderer');
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Node center in flow coordinates
    const nodeX = node.position.x + 110;
    const nodeY = node.position.y + getNodeHeight(node) / 2;

    // Calculate current visible area center
    const viewCenterX = -x / zoom + containerWidth / (2 * zoom);
    const viewCenterY = -y / zoom + containerHeight / (2 * zoom);

    // Calculate how much to pan
    const deltaX = nodeX - viewCenterX;
    const deltaY = nodeY - viewCenterY;

    // Check if node is too far from current view center
    const threshold = 200; // distance in flow coordinates
    if (Math.abs(deltaX) > threshold || Math.abs(deltaY) > threshold) {
        // Calculate new pan values, keeping the zoom the same
        const newX = x - deltaX * zoom;
        const newY = y - deltaY * zoom;

        // Use a smooth animation to pan
        reactFlowInstance.setViewport({ x: newX, y: newY, zoom }, { duration: 300 });
    }
};

const WorkflowCanvas = forwardRef((props, ref) => {
    const { workflow, onWorkflowChange, onNodeConfig } = props;
    const reactFlowWrapper = useRef(null);

    // Nodes & Edges state
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // Additional state
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedEdge, setSelectedEdge] = useState(null);
    const [nodePaletteVisible, setNodePaletteVisible] = useState(true);
    const [nodePalettePosition, setNodePalettePosition] = useState('left'); // 'left' or 'right'
    const [reactFlowInstance, setReactFlowInstance] = useState(null);

    // Node counter for unique IDs
    const [nodeCounter, setNodeCounter] = useState(1);

    // Load workflow when it changes
    useEffect(() => {
        if (workflow) {
            if (workflow.nodes && workflow.edges) {
                setNodes(workflow.nodes);
                setEdges(workflow.edges);
                updateNodeCounter(workflow.nodes);
            }  else {
                const containerRect = reactFlowWrapper.current.getBoundingClientRect();
                const initialPosition = getInitialPosition(containerRect, nodePaletteVisible);
                const initialNodes = getInitialNodes(initialPosition);
                setNodes(initialNodes);
                setEdges([]);
                handleWorkflowChange(initialNodes, []);
                setSelectedNode(initialNodes[0]);
            }
        }
    }, [workflow?.id, nodePaletteVisible]);


    // Set initial viewport when React Flow instance is ready
    useEffect(() => {
        if (reactFlowInstance) {
            // Set initial zoom level after instance is ready
            setTimeout(() => {
                //reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 0.35 });
                //reactFlowInstance.fitView({ padding: 0.5, duration: 800 });
            }, 100);
        }
    }, [reactFlowInstance]);

    // Update node counter based on existing nodes
    const updateNodeCounter = (nodes) => {
        let maxCounter = 0;
        nodes.forEach(node => {
            const idParts = node.id.split('-');
            if (idParts.length > 1) {
                const num = parseInt(idParts[1]);
                if (!isNaN(num) && num > maxCounter) {
                    maxCounter = num;
                }
            }
        });
        setNodeCounter(maxCounter + 1);
    };

    // Exposed methods via ref
    useImperativeHandle(ref, () => ({
        getNodes: () => nodes,
        getEdges: () => edges,
        updateNode: (updatedNode) => {
            setNodes(nodes.map(node =>
                node.id === updatedNode.id ? updatedNode : node
            ));

            // Refresh workflow config
            handleWorkflowChange(
                nodes.map(node => node.id === updatedNode.id ? updatedNode : node),
                edges
            );
        },
        clearCanvas: () => {
            const containerRect = reactFlowWrapper.current.getBoundingClientRect();
            const initialPosition = getInitialPosition(containerRect, nodePaletteVisible);
            const initialNodes = getInitialNodes(initialPosition);

            setNodes(initialNodes);
            setEdges([]);
            handleWorkflowChange(initialNodes, []);
            setNodeCounter(1);
            setSelectedNode(initialNodes[0]);
            // Reset view with the same zoom and without an animated "fitView"
            /*if (reactFlowInstance) {
                setTimeout(() => {
                    reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 0.35 });
                }, 50);
            }*/
        }


    }));

    // Update parent component with workflow changes
    const handleWorkflowChange = useCallback((updatedNodes, updatedEdges) => {
        if (onWorkflowChange && workflow) {
            onWorkflowChange({
                ...workflow,
                nodes: updatedNodes,
                edges: updatedEdges
            });
        }
    }, [workflow, onWorkflowChange]);

    // Handle node selection
    const handleNodeClick = (event, node) => {
        setSelectedNode(node);
        setSelectedEdge(null);
    };

    // Handle edge selection
    const handleEdgeClick = (event, edge) => {
        setSelectedEdge(edge);
        setSelectedNode(null);
    };

    // Handle node double-click to open config modal
    const handleNodeDoubleClick = (event, node) => {
        if (onNodeConfig) {
            onNodeConfig(node);
        }
    };

    // Handle node deletion
    const handleDeleteNode = () => {
        if (!selectedNode) return;

        if (selectedNode.type === 'triggerNode') {
            message.error('Cannot delete the trigger node.');
            return;
        }

        // Remove related edges
        const remainingEdges = edges.filter(
            edge => edge.source !== selectedNode.id && edge.target !== selectedNode.id
        );

        // Remove the node
        const remainingNodes = nodes.filter(node => node.id !== selectedNode.id);

        setNodes(remainingNodes);
        setEdges(remainingEdges);
        setSelectedNode(null);

        // Update workflow
        handleWorkflowChange(remainingNodes, remainingEdges);
    };

    // Handle edge deletion
    const handleDeleteEdge = () => {
        if (!selectedEdge) return;

        const remainingEdges = edges.filter(edge => edge.id !== selectedEdge.id);
        setEdges(remainingEdges);
        setSelectedEdge(null);

        // Update workflow
        handleWorkflowChange(nodes, remainingEdges);
    };

    // Handle adding a connection (edge) between nodes
    const handleConnect = useCallback((connection) => {
        // Check if connection already exists
        const connectionExists = edges.some(
            edge => edge.source === connection.source &&
                edge.target === connection.target &&
                edge.sourceHandle === connection.sourceHandle
        );

        if (connectionExists) {
            message.warning('Connection already exists.');
            return;
        }

        // Create edge with proper marker and styling
        const newEdge = {
            ...connection,
            id: `edge-${connection.source}-${connection.sourceHandle || 'default'}-${connection.target}`,
            type: 'default',
            animated: false,
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20
            },
            style: {
                strokeWidth: 2
            }
        };

        const sourceNode = nodes.find(node => node.id === connection.source);
        const targetNode = nodes.find(node => node.id === connection.target);
        const styledEdge = styleEdgeBasedOnNodes(newEdge, sourceNode, targetNode);
        const updatedEdges = addEdge(newEdge, edges);
        setEdges(updatedEdges);

        // Update workflow
        handleWorkflowChange(nodes, updatedEdges);
    }, [edges, nodes, handleWorkflowChange]);

    // Handle pane click to clear selection
    const handlePaneClick = () => {
        setSelectedNode(null);
        setSelectedEdge(null);
    };

    // Helper function to style an edge based on its source node type and source handle
    const styleEdgeBasedOnNodes = (edge, sourceNode, targetNode) => {
        let sourceStyled = false;

        // First, apply source node styling if applicable.
        if (sourceNode?.type === 'conditionalNode') {
            if (edge.sourceHandle === 'true') {
                edge.label = 'True';
                edge.labelStyle = { fill: '#22c55e', fontWeight: 'bold' };
                edge.style = { ...edge.style, stroke: '#22c55e' };
                sourceStyled = true;
            } else if (edge.sourceHandle === 'false') {
                edge.label = 'False';
                edge.labelStyle = { fill: '#ef4444', fontWeight: 'bold' };
                edge.style = { ...edge.style, stroke: '#ef4444' };
                sourceStyled = true;
            }
        }
        if (sourceNode?.type === 'switchNode' && edge.sourceHandle) {
            const switchValue = edge.sourceHandle.replace('switch-', '');
            edge.label = switchValue;
            edge.labelStyle = { fill: '#3b82f6', fontWeight: 'bold' };
            edge.style = { ...edge.style, stroke: '#3b82f6' };
            sourceStyled = true;
        }

        // Then, if no source styling was applied, apply target styling.
        if (!sourceStyled) {
            if (targetNode?.type === 'completeNode') {
                edge.label = 'Complete';
                edge.labelStyle = { fill: '#22c55e', fontWeight: 'bold' };
                edge.style = { ...edge.style, stroke: '#22c55e' };
            } else if (targetNode?.type === 'errorNode') {
                edge.label = 'Error';
                edge.labelStyle = { fill: '#ef4444', fontWeight: 'bold' };
                edge.style = { ...edge.style, stroke: '#ef4444' };
            }
        }

        return edge;
    };



    // Add a new node from the node palette

    const handleAddNode = (nodeType, category) => {

        const nodeId = `${nodeType}-${nodeCounter}`;
        setNodeCounter(prevCounter => prevCounter + 1);

        // Determine position relative to selected node if any
        const position = getPositionForNewNode(selectedNode, nodeType, edges);

        let newNode = {
            id: nodeId,
            position,
            data: { label: getNodeLabel(nodeType) }
        };

        // Configure node based on type
        switch (nodeType) {
            case 'trigger':
                newNode.type = 'triggerNode';
                newNode.data.triggerType = 'callEvaluation';
                newNode.data.config = { type: 'callEvaluation' };
                break;

            case 'conditional':
                newNode.type = 'conditionalNode';
                newNode.data.conditionType = 'javascript';
                newNode.data.config = {
                    type: 'javascript',
                    condition: 'record.qualScore > 80'
                };
                break;

            case 'switch':
                newNode.type = 'switchNode';
                newNode.data.switchType = 'javascript';
                newNode.data.outputs = ['Option 1', 'Option 2', 'Default'];
                newNode.data.config = {
                    type: 'javascript',
                    expression: 'record.callType',
                    outputs: ['Option 1', 'Option 2', 'Default']
                };
                break;

            case 'email':
                newNode.type = 'actionNode';
                newNode.data.actionType = 'email';
                newNode.data.config = {
                    type: 'email',
                    template: 'default',
                    subject: 'New Call Notification',
                    recipients: []
                };
                break;

            case 'webhook':
                newNode.type = 'actionNode';
                newNode.data.actionType = 'webhook';
                newNode.data.config = {
                    type: 'webhook',
                    url: '',
                    method: 'POST',
                    headers: {},
                    body: '{}'
                };
                break;

            case 'updateRecord':
                newNode.type = 'actionNode';
                newNode.data.actionType = 'updateRecord';
                newNode.data.config = {
                    type: 'updateRecord',
                    updateType: 'javascript',
                    script: 'return { extraData: { processed: true } };'
                };
                break;

            case 'complete':
                newNode.type = 'completeNode';
                newNode.data.config = { type: 'complete' };
                break;

            case 'error':
                newNode.type = 'errorNode';
                newNode.data.config = {
                    type: 'error',
                    message: 'Workflow error occurred'
                };
                break;

            default:
                return;
        }

        const updatedNodes = [...nodes, newNode];
        setNodes(updatedNodes);

        // Try to create a connection if there's a selected node
        if (selectedNode) {
            const newConnection = tryConnectNodes(selectedNode, newNode, edges);
            if (newConnection) {
                styleEdgeBasedOnNodes(newConnection,selectedNode,newNode);
                const updatedEdges = [...edges, newConnection];
                setEdges(updatedEdges);
                handleWorkflowChange(updatedNodes, updatedEdges);
                if (!selectedNode || !findFreeConnectionPoint(selectedNode, 'output', updatedEdges)) {
                    setSelectedNode(newNode);
                    console.log("setting selected node");
                }
                console.log("node added");
            } else {
                handleWorkflowChange(updatedNodes, edges);
                setSelectedNode(newNode);
            }
        } else {
            // Update workflow without new connection
            handleWorkflowChange(updatedNodes, edges);
            setSelectedNode(newNode);
        }




        // Pan to the new node without changing zoom
        if (reactFlowInstance) {
            setTimeout(() => {
                panToNode(reactFlowInstance, newNode);
            }, 50);
        }
    };

    // Clear the canvas
    const handleClearCanvas = () => {
        Modal.confirm({
            title: 'Clear Workflow',
            content: 'Are you sure you want to clear the entire workflow? This action cannot be undone.',
            okText: 'Clear',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => {
                // Get the container dimensions and compute the initial position
                reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 });
                const containerRect = reactFlowWrapper.current.getBoundingClientRect();
                const initialPosition = getInitialPosition(containerRect, nodePaletteVisible);
                const initialNodes = getInitialNodes(initialPosition);

                setNodes(initialNodes);
                setEdges([]);
                setSelectedNode(null);
                setSelectedEdge(null);
                setNodeCounter(1);
                setSelectedNode(initialNodes[0]);
                // Update workflow
                handleWorkflowChange(initialNodes, []);
            }
        });
    };



    // Get appropriate label for node type
    const getNodeLabel = (nodeType) => {
        switch (nodeType) {
            case 'trigger': return 'Trigger';
            case 'conditional': return 'Conditional';
            case 'switch': return 'Switch';
            case 'email': return 'Send Email';
            case 'webhook': return 'Web Hook';
            case 'updateRecord': return 'Update Record';
            case 'complete': return 'Complete';
            case 'error': return 'Error';
            default: return 'Node';
        }
    };

    // Toggle node palette position
    const togglePalettePosition = () => {
        setNodePalettePosition(prev => prev === 'left' ? 'right' : 'left');
    };

    return (
        <div
            ref={reactFlowWrapper}
            style={{
                height: 'calc(100vh - 190px)',
                minHeight: '600px',
                width: '100%',
                border: '1px solid #f0f0f0',
                borderRadius: '8px'
            }}
        >
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={handleConnect}
                onNodeClick={handleNodeClick}
                onNodeDoubleClick={handleNodeDoubleClick}
                onEdgeClick={handleEdgeClick}
                onPaneClick={handlePaneClick}
                onInit={setReactFlowInstance}
                nodeTypes={nodeTypes}
                deleteKeyCode="Delete"
                multiSelectionKeyCode="Control"
                defaultEdgeOptions={{
                    type: 'default',
                    markerEnd: {
                        type: MarkerType.ArrowClosed
                    },
                    style: {
                        strokeWidth: 2
                    }
                }}


                // Important settings for better usability
                connectionRadius={30}
                connectionMode="loose"
                minZoom={0.1}
                maxZoom={2}
                fitView={false} // Disable automatic fitView
                fitViewOptions={{
                    padding: 0.5
                }}
                connectionLineStyle={{
                    strokeWidth: 2,
                    stroke: '#1890ff'
                }}
                snapToGrid={true}
                snapGrid={[16, 16]}
                elevateNodesOnSelect={true}
                nodesDraggable={true}
                nodesConnectable={true}
                nodesFocusable={true}
            >
                <Background color="#f0f0f0" gap={16} />

                {/* Node palette */}
                {nodePaletteVisible && (
                    <NodePalette
                        position={nodePalettePosition}
                        onTogglePosition={togglePalettePosition}
                        onAddNode={handleAddNode}
                        onClearCanvas={handleClearCanvas}
                    />
                )}

                {/* Move Controls to bottom-right */}
                <Controls
                    position="bottom-right"
                    showInteractive={true}
                    style={{
                        bottom: 20,
                        right: 20
                    }}
                />

                {/* Context menu when node or edge is selected */}
                {(true || selectedNode || selectedEdge ) && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            background: 'white',
                            borderRadius: 4,
                            padding: '4px 8px',
                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            gap: 8,
                            zIndex: 10,
                        }}
                    >
                        {selectedNode && (
                            <>
                                <Tooltip title="Configure Node">
                                    <Button
                                        type="text"
                                        icon={<SettingOutlined style={{ fontSize: 16 }} />}
                                        onClick={() => onNodeConfig(selectedNode)}
                                    />
                                </Tooltip>
                                {selectedNode.type !== 'triggerNode' && (
                                    <Tooltip title="Delete Node">
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined style={{ fontSize: 16 }} />}
                                            onClick={handleDeleteNode}
                                        />
                                    </Tooltip>
                                )}
                            </>
                        )}
                        {selectedEdge && (
                            <Tooltip title="Delete Connection">
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined style={{ fontSize: 16 }} />}
                                    onClick={handleDeleteEdge}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title="Clear Canvas">
                            <Button
                                type="text"
                                danger
                                icon={<ClearOutlined style={{ fontSize: 16 }} />}
                                onClick={handleClearCanvas}
                            />
                        </Tooltip>
                    </div>
                )}

            </ReactFlow>
        </div>
    );
});

export default WorkflowCanvas;
